/**
 * GET para Google Apps Script (Web App /exec).
 * Tenta fetch; se vier HTML ou CORS falhar, usa JSONP (callback).
 * Requer Apps Script v6.2+ com suporte a ?callback= no doGet.
 */

const STORAGE_KEY_API = 'copa-sheets-api-publico-ade';

export function normalizarEndpointGas(raw: string): string {
  return String(raw || '').trim().replace(/\/+$/, '');
}

/** Só aceita URL de Web App do Google Apps Script. */
export function endpointGasValido(endpoint: string): boolean {
  const u = normalizarEndpointGas(endpoint);
  return /^https:\/\/script\.google\.com\/macros\/s\/[^/]+\/exec(\?.*)?$/i.test(u);
}

/** Grava endpoint quando o link traz ?api=... */
export function persistirEndpointGas(endpoint: string): void {
  const v = normalizarEndpointGas(endpoint);
  if (!v || !endpointGasValido(v)) return;
  try {
    localStorage.setItem(STORAGE_KEY_API, v);
  } catch {
    /* quota */
  }
}

/** Usa ?api= da URL ou o último salvo no celular. */
export function resolverEndpointGas(daUrl: string): string {
  const daQuery = normalizarEndpointGas(daUrl);
  if (daQuery) {
    if (endpointGasValido(daQuery)) return daQuery;
    return '';
  }
  try {
    const salvo = normalizarEndpointGas(localStorage.getItem(STORAGE_KEY_API) || '');
    if (salvo && !endpointGasValido(salvo)) {
      localStorage.removeItem(STORAGE_KEY_API);
      return '';
    }
    return salvo;
  } catch {
    return '';
  }
}

function montarUrlGas(endpoint: string, params: Record<string, string>): string {
  const base = normalizarEndpointGas(endpoint);
  const qs = new URLSearchParams(params);
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}${qs.toString()}`;
}

function respostaPareceHtml(text: string): boolean {
  const t = text.trim().toLowerCase();
  return t.startsWith('<!doctype') || t.startsWith('<html') || (t.startsWith('<') && t.includes('<head'));
}

function fetchJsonp<T>(url: string, callbackName: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    const w = window as unknown as Record<string, unknown>;

    const cleanup = () => {
      delete w[callbackName];
      script.remove();
    };

    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('Tempo esgotado ao buscar dados da planilha.'));
    }, 45000);

    w[callbackName] = (data: T) => {
      window.clearTimeout(timer);
      cleanup();
      resolve(data);
    };

    script.onerror = () => {
      window.clearTimeout(timer);
      cleanup();
      reject(
        new Error(
          'Não foi possível conectar à planilha. Verifique a internet ou peça ao organizador para atualizar o script (v6.2).'
        )
      );
    };

    script.src = url;
    document.head.appendChild(script);
  });
}

function deveTentarJsonp(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  if (err instanceof SyntaxError) return true;
  if (err instanceof Error) {
    const m = err.message.toLowerCase();
    return (
      m.includes('failed to fetch') ||
      m.includes('networkerror') ||
      m.includes('load failed') ||
      m.includes('network request failed') ||
      m.includes('unexpected token') ||
      m.includes('resposta html') ||
      m.includes('não é json')
    );
  }
  return false;
}

function parseJsonGas<T>(text: string): T {
  const trimmed = text.trim();
  if (!trimmed) throw new Error('Planilha respondeu vazio.');
  if (respostaPareceHtml(trimmed)) {
    throw new TypeError('Resposta HTML em vez de JSON (endereço da planilha incorreto).');
  }
  try {
    return JSON.parse(trimmed) as T;
  } catch {
    throw new SyntaxError('A planilha não retornou JSON válido.');
  }
}

/** GET JSON no Apps Script — fetch com fallback JSONP. */
export async function fetchGasGet<T>(
  endpoint: string,
  params: Record<string, string> = {}
): Promise<T> {
  const base = normalizarEndpointGas(endpoint);
  if (!endpointGasValido(base)) {
    throw new Error(
      'Endereço da planilha inválido. O organizador deve configurar a URL do Google Apps Script (termina em /exec) em Resultados da Copa e enviar um link novo com ?api=.'
    );
  }

  const url = montarUrlGas(base, params);
  try {
    const resp = await fetch(url, {
      method: 'GET',
      redirect: 'follow',
      cache: 'no-store',
    });
    const text = await resp.text();
    if (!resp.ok) {
      if (respostaPareceHtml(text)) {
        throw new TypeError('Resposta HTML em vez de JSON.');
      }
      throw new Error(`HTTP ${resp.status}`);
    }
    return parseJsonGas<T>(text);
  } catch (err) {
    if (!deveTentarJsonp(err)) throw err;
    const callbackName = `gasCb_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    const jsonpUrl = montarUrlGas(base, { ...params, callback: callbackName });
    return fetchJsonp<T>(jsonpUrl, callbackName);
  }
}

/** POST JSON no Apps Script (organizador — excluir, corrigir, liberarmm). */
export async function fetchGasPost<T>(
  endpoint: string,
  body: Record<string, unknown>
): Promise<T> {
  const base = normalizarEndpointGas(endpoint);
  if (!endpointGasValido(base)) {
    throw new Error('Endereço da planilha inválido.');
  }
  const resp = await fetch(base, {
    method: 'POST',
    redirect: 'follow',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify(body),
  });
  const text = await resp.text();
  if (!resp.ok) {
    if (respostaPareceHtml(text)) {
      throw new TypeError('Resposta HTML em vez de JSON.');
    }
    throw new Error(`HTTP ${resp.status}`);
  }
  return parseJsonGas<T>(text);
}
